import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Cached empty state HTML from initial server render
   * @type {string | null}
   */
  #cachedEmptyState = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.predictiveSearchResults) {
      this.#cachedEmptyState = this.refs.predictiveSearchResults.innerHTML;
      this.#applyCardSizeToResults();
    }

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  /**
   * Handles the dialog open event to focus the search input.
   */
  #handleDialogOpen = () => {
    this.refs.searchInput?.focus();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        if (container.classList.contains('predictive-search-results__wrapper-queries')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__query-link'));
        }
        if (container.classList.contains('predictive-search-results__wrapper-collections')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__collections-link'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    const viewAllButton = this.querySelector('.predictive-search__view-all');
    if (viewAllButton instanceof HTMLElement) {
      // Find the index of the first .predictive-search-results__card element
      const firstCardIndex = allItems.findIndex((item) =>
        item.classList.contains('predictive-search-results__card')
      );
      
      if (firstCardIndex >= 0) {
        // Insert view all button before the first card
        allItems.splice(firstCardIndex, 0, viewAllButton);
      } else {
        // If no card found, append to the end as fallback
        allItems.push(viewAllButton);
      }
    }

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) {
      this.refs.searchInput.setAttribute('aria-activedescendant', '')
      return
    }

    if(index < 0) {
      this.refs.searchInput.setAttribute('aria-activedescendant', '')
    }

    for (const item of this.#allResultsItems) {
      item.classList.remove('keyboard-focus');
    }

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
        this.refs.searchInput.setAttribute('aria-activedescendant', item.id)
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          const currentItem = this.#currentItem;
          if (currentItem instanceof HTMLElement) {
            // If currentItem is a button (view all button), click it directly
            if (currentItem.tagName === 'BUTTON') {
              currentItem.click();
            }
            // If currentItem is already an <a> element (queries, collections), click it directly
            else if (currentItem.tagName === 'A') {
              currentItem.click();
            }
            // Otherwise, find the <a> element inside it (products)
            else {
              const link = currentItem.querySelector('a');
              link?.click();
            }
          }
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };


  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  get #cardSizeFromSettings() {
    const headerSearch = document.querySelector('[data-header-search-settings]');
    const inModal = this.closest('.search-modal');
    return {
      productCardSize: inModal
        ? (headerSearch?.dataset?.headerSearchCardSize || this.dataset.productCardSize || 'medium')
        : (this.dataset.productCardSize || headerSearch?.dataset?.headerSearchCardSize || 'medium'),
      mobileProductCardSize: inModal
        ? (headerSearch?.dataset?.headerSearchMobileCardSize || this.dataset.mobileProductCardSize || 'small')
        : (this.dataset.mobileProductCardSize || headerSearch?.dataset?.headerSearchMobileCardSize || 'small')
    };
  }

  /** Fallback when morphed HTML has no data-ps-card-size-px; keep in sync with util-predictive-search-card-size.liquid */
  #psCardSizeToPx(size) {
    const map = { small: '180px', medium: '290px', large: '365px', 'extra-large': '530px' };
    return map[size] ?? '290px';
  }

  #psMobileColumns(size) {
    return size === 'large' ? '1fr' : 'repeat(2, 1fr)';
  }

  #applyCardSizeToResults() {
    const { productCardSize, mobileProductCardSize } = this.#cardSizeFromSettings;
    const cardSizePx = this.#psCardSizeToPx(productCardSize);
    const mobileColumns = this.#psMobileColumns(mobileProductCardSize);
    const targets = this.refs.predictiveSearchResults.querySelectorAll(
      '.predictive-search-results__wrapper-products'
    );
    for (const el of targets) {
      if (!(el instanceof HTMLElement)) continue;
      el.style.setProperty('--ps-card-size', cardSizePx);
      el.style.setProperty('--ps-mobile-columns', mobileColumns);
    }
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const { productCardSize, mobileProductCardSize } = this.#cardSizeFromSettings;
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('product_card_size', productCardSize);
    url.searchParams.set('mobile_product_card_size', mobileProductCardSize);

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);
        this.#applyCardSizeToResults();
        this.#resetScrollPositions();
        // Notify Swym to re-initialize wishlist buttons on search results
        document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }


  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const emptyStateElement = predictiveSearchResults.querySelector('.predictive-search-results__inner--empty-state');
    if (emptyStateElement) {
      this.#resetScrollPositions();
      return;
    }

    if (this.#cachedEmptyState) {
      predictiveSearchResults.innerHTML = this.#cachedEmptyState;
      this.#applyCardSizeToResults();
      this.#resetScrollPositions();
      // Notify Swym after reset to cached empty state
      document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
      return;
    }

    const emptySectionId = 'predictive-search-empty';
    const abortController = this.#createAbortController();
    const { productCardSize, mobileProductCardSize } = this.#cardSizeFromSettings;
    const rootUrl = new URL('/', window.location.origin);
    rootUrl.searchParams.set('product_card_size', productCardSize);
    rootUrl.searchParams.set('mobile_product_card_size', mobileProductCardSize);
    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, rootUrl);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#applyCardSizeToResults();
    this.#resetScrollPositions();
    // Notify Swym after reset to fetched empty section
    document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
